/**
 * Legacy Storage Hook
 * For modules: Crew, Drills, Defects, Maintenance, Logbook, Dashboard
 * Uses vessel-scoped localStorage keys: ${dataType}_${vesselId}
 */

import { devLog } from '../utils/devLog';
import { useEffect, useMemo, useCallback } from 'react';
import { useVessel } from '../contexts/VesselContext';
import { readJSON, writeJSON, removeItem } from '../lib/storage';

export const useLegacyStorage = () => {
  const { currentVesselId } = useVessel();
  
  if (!currentVesselId) {

  }
  
  const vesselId = currentVesselId || 'default';
  
  // Listen for hydration events from sync bridge
  useEffect(() => {
    const onHydrate = (e) => {
      const detail = e.detail || {};
      // FIXED 2026-02-16: Support both 'module' (modern) and 'dataType' (legacy) formats
      const moduleType = detail.module || detail.dataType;
      const vid = detail.vesselId;
      const value = detail.value;

      // Safety check - must have module/dataType before processing
      if (!moduleType) {
        console.warn('[useLegacyStorage] Hydration event missing module/dataType:', detail);
        return;
      }

      if (vid !== vesselId) return;
      const key = `${moduleType}_${vid}`;
      // PHASE C FIX: Use writeJSON for org-scoped keys
      writeJSON(key, value);
    };
    window.addEventListener('sr:sync:hydrate', onHydrate);
    return () => window.removeEventListener('sr:sync:hydrate', onHydrate);
  }, [vesselId]);
  
  /**
   * Get storage key with vessel scope
   * @param {string} dataType - The type of data (e.g., 'crewMembers', 'drills')
   * @returns {string} Vessel-scoped storage key
   */
  const getKey = useCallback((dataType) => {
    if (!dataType) {
      throw new Error('[useLegacyStorage] dataType is required');
    }
    return `${dataType}_${vesselId}`;
  }, [vesselId]);
  
  /**
   * Get data from vessel-scoped storage
   * PHASE C FIX: Now delegates to lib/storage.js for org-scoped keys
   * @param {string} dataType - The type of data
   * @param {*} defaultValue - Default value if not found
   * @returns {*} Parsed data or default value
   */
  const get = useCallback((dataType, defaultValue = null) => {
    try {
      const key = getKey(dataType);
      return readJSON(key, defaultValue);
    } catch (error) {
      console.error('[useLegacyStorage] get error:', error);
      return defaultValue;
    }
  }, [getKey]);
  
  /**
   * Set data in vessel-scoped storage
   * PHASE C FIX: Now delegates to lib/storage.js for org-scoped keys
   * writeJSON handles org-scoping and emits sr:storage:set event
   * @param {string} dataType - The type of data
   * @param {*} value - Value to store
   */
  const set = useCallback((dataType, value) => {
    try {
      const key = getKey(dataType);
      devLog('[useLegacyStorage.set] Writing:', dataType, 'key:', key);
      writeJSON(key, value);
    } catch (error) {
      console.error('[useLegacyStorage] set error:', error);
      throw error;
    }
  }, [getKey]);
  
  /**
   * Remove data from vessel-scoped storage
   * PHASE C FIX: Now delegates to lib/storage.js for org-scoped keys
   * @param {string} dataType - The type of data
   */
  const remove = useCallback((dataType) => {
    try {
      const key = getKey(dataType);
      removeItem(key);
    } catch (error) {
      console.error('[useLegacyStorage] remove error:', error);
    }
  }, [getKey]);
  
  // Common legacy storage keys for convenience - memoized
  const keys = useMemo(() => ({
    crew: () => getKey('crewMembers'),
    drills: () => getKey('drills'),
    defects: () => getKey('defects'),
    maintenance: () => getKey('maintenance_history'),
    equipment: () => getKey('equipment_records'),
    logEntries: () => getKey('log_entries'),
    voyages: () => getKey('voyages'),
    passagePlans: () => getKey('passage_plans'),
    equipmentChecks: () => getKey('equipmentChecks'),
  }), [getKey]);
  
  // Return stable object for React deps
  return useMemo(() => ({
    getKey,
    get,
    set,
    remove,
    keys,
    vesselId
  }), [getKey, get, set, remove, keys, vesselId]);
};